import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, CircleHelp, Mic, PauseCircle, Users, Wifi, XCircle } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { summarize, type ListenerState, type VoicePath, type VoicePathTone } from "../lib/voicePath";
import { useVoicePathStore } from "../store/voicePathStore";

const TONE_DOT: Record<VoicePathTone, string> = {
  good: "bg-emerald-500",
  warn: "bg-amber-500",
  bad: "bg-red-500",
  idle: "bg-muted-foreground/60",
};
const TONE_TEXT: Record<VoicePathTone, string> = {
  good: "text-emerald-700 dark:text-emerald-300",
  warn: "text-amber-700 dark:text-amber-300",
  bad: "text-red-700 dark:text-red-300",
  idle: "text-muted-foreground",
};
const LISTENER: Record<ListenerState, { icon: LucideIcon; cls: string }> = {
  hears: { icon: CheckCircle2, cls: "text-emerald-600 dark:text-emerald-400" },
  paused: { icon: PauseCircle, cls: "text-amber-600 dark:text-amber-400" },
  missing: { icon: XCircle, cls: "text-red-600 dark:text-red-400" },
  unknown: { icon: CircleHelp, cls: "text-muted-foreground" },
};

/** Three signal bars; `filled` of them coloured by tone. */
function Bars({ filled, tone, size = "sm" }: { filled: number; tone: VoicePathTone; size?: "sm" | "md" }) {
  const heights = size === "sm" ? [3, 5, 7] : [5, 8, 11];
  return (
    <span className="inline-flex items-end gap-[1.5px]" aria-hidden>
      {heights.map((h, i) => (
        <span
          key={h}
          style={{ height: h }}
          className={`${size === "sm" ? "w-[2px]" : "w-[3px]"} rounded-full ${i < filled ? TONE_DOT[tone] : "bg-muted-foreground/30"}`}
        />
      ))}
    </span>
  );
}

/**
 * The "is my voice getting through?" badge on the in-call mic button, and
 * its popover: mic, network and every listener (see `lib/voicePath.ts`).
 * Renders nothing outside a live call.
 */
export function VoicePathBadge({ light }: { light?: boolean }) {
  const { t } = useTranslation();
  const path = useVoicePathStore((s) => s.path);
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  if (!path) return null;
  const summary = summarize(path);
  const label = t("call.voicePath.badgeLabel", { summary: t(`call.voicePath.summary.${summary.tone}`) });

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={label}
        aria-label={label}
        aria-expanded={open}
        className={`absolute -top-1.5 -left-1.5 z-10 size-5 rounded-full border-2 flex items-center justify-center bg-background ${
          light ? "border-background" : "border-foreground"
        } ring-1 ring-border hover:bg-muted transition-colors`}
      >
        <Bars filled={summary.bars} tone={summary.tone} />
      </button>
      {open && <VoicePathPopover path={path} anchor={buttonRef} onClose={() => setOpen(false)} />}
    </>
  );
}

function VoicePathPopover({
  path,
  anchor,
  onClose,
}: {
  path: VoicePath;
  anchor: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const level = useVoicePathStore((s) => s.level);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ left: number; bottom: number } | null>(null);
  const summary = summarize(path);

  // Fixed, above the badge and kept inside the viewport: the mic button
  // lives in the call bar, the floating widget and a phone's bottom bar.
  useLayoutEffect(() => {
    const place = () => {
      const rect = anchor.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(320, window.innerWidth - 16);
      const left = Math.min(Math.max(8, rect.left - 8), window.innerWidth - width - 8);
      setPos({ left, bottom: window.innerHeight - rect.top + 8 });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [anchor]);

  useEffect(() => {
    const onPointer = (e: PointerEvent) => {
      if (!(e.target instanceof Node)) return;
      if (panelRef.current?.contains(e.target) || anchor.current?.contains(e.target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchor, onClose]);

  const sending = path.mic === "live" || path.mic === "silent";
  const others = path.listeners;

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={t("call.voicePath.title")}
      style={pos ? { left: pos.left, bottom: pos.bottom, width: Math.min(320, window.innerWidth - 16) } : { visibility: "hidden" }}
      className="fixed z-[1000] rounded-2xl border border-border bg-background text-foreground shadow-xl p-4 text-sm"
    >
      <div className="flex items-center gap-2">
        <Bars filled={summary.bars} tone={summary.tone} size="md" />
        <p className="font-semibold">{t("call.voicePath.title")}</p>
        <span className={`ms-auto text-xs font-semibold ${TONE_TEXT[summary.tone]}`}>
          {t(`call.voicePath.summary.${summary.tone}`)}
        </span>
      </div>

      <ul className="mt-3 space-y-3">
        <Row icon={Mic} label={t("call.voicePath.mic.label")} tone={path.mic === "silent" ? "warn" : path.mic === "live" ? "good" : "idle"}>
          {t(`call.voicePath.mic.${path.mic}`)}
          {path.mic === "live" && (
            <span className="mt-1.5 block h-1.5 rounded-full bg-muted overflow-hidden" aria-hidden>
              <span
                className="block h-full bg-emerald-500 transition-[width] duration-200"
                style={{ width: `${Math.round(Math.min(1, (level ?? 0) * 1.6) * 100)}%` }}
              />
            </span>
          )}
        </Row>

        <Row
          icon={Wifi}
          label={t("call.voicePath.network.label")}
          tone={path.network === "good" ? "good" : path.network === "weak" ? "warn" : path.network === "down" ? "bad" : "idle"}
        >
          {t(`call.voicePath.network.${path.network}`)}
          {(path.lossPct !== null || path.rttMs !== null) && (
            <span className="block text-xs text-muted-foreground mt-0.5" dir="ltr">
              {t("call.voicePath.network.details", {
                loss: path.lossPct === null ? "–" : path.lossPct.toFixed(path.lossPct < 1 ? 1 : 0),
                rtt: path.rttMs ?? "–",
              })}
            </span>
          )}
        </Row>

        <Row
          icon={Users}
          label={t("call.voicePath.listeners.label")}
          tone={!sending ? "idle" : others.some((l) => l.state === "missing") ? "bad" : others.some((l) => l.state === "paused") ? "warn" : "good"}
        >
          {others.length === 0
            ? t("call.voicePath.listeners.alone")
            : !sending
              ? t("call.voicePath.listeners.muted")
              : t("call.voicePath.listeners.heardBy", { heard: summary.heardBy, total: others.length })}
          {sending && others.length > 0 && (
            <ul className="mt-2 space-y-1.5 max-h-48 overflow-y-auto">
              {others.map((l) => {
                const { icon: Icon, cls } = LISTENER[l.state];
                return (
                  <li key={l.identity} className="flex items-start gap-2">
                    <Icon className={`size-4 shrink-0 mt-px ${cls}`} aria-hidden />
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{l.name}</span>
                      <span className="block text-xs text-muted-foreground">{t(`call.voicePath.listeners.${l.state}`)}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </Row>
      </ul>

      <p className="mt-3 text-xs text-muted-foreground">{t("call.voicePath.footnote")}</p>
    </div>,
    document.body,
  );
}

function Row({
  icon: Icon,
  label,
  tone,
  children,
}: {
  icon: LucideIcon;
  label: string;
  tone: VoicePathTone;
  children: React.ReactNode;
}) {
  return (
    <li className="flex items-start gap-3">
      <span className="relative mt-0.5 size-7 shrink-0 rounded-lg bg-muted flex items-center justify-center">
        <Icon className="size-4" aria-hidden />
        <span className={`absolute -bottom-0.5 -end-0.5 size-2.5 rounded-full border-2 border-background ${TONE_DOT[tone]}`} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</span>
        <span className="block">{children}</span>
      </span>
    </li>
  );
}
